import { useMemo } from 'react'

import {
  DonutChart,
  STAT_GRID_CLASS,
  StatCard,
  StatCardsSkeleton,
  StatText,
} from '@/components/stats/StatCard'
import { Badge } from '@/components/ui/badge'
import type { ChartConfig } from '@/components/ui/chart'
import type { Entitlement, SubscriptionStatusResponse } from '@/types/api/subscription'
import { SubscriptionStatus } from '@/types/enums'
import { formatDate, parseDate } from '@/utils/date'
import { numericLimit } from '@/utils/quota'

interface SubscriptionStatsProps {
  status: SubscriptionStatusResponse | undefined
  usage: Entitlement[] | undefined
  isLoading?: boolean
}

export function SubscriptionStats({ status, usage, isLoading = false }: SubscriptionStatsProps) {
  const daysRemaining = useMemo(() => {
    if (!status?.currentPeriodEnd) return null
    const end = parseDate(status.currentPeriodEnd)
    if (!end) return null
    const now = new Date()
    const diffTime = end.getTime() - now.getTime()
    return Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)))
  }, [status?.currentPeriodEnd])

  const quotaStats = useMemo(() => {
    const items = usage ?? []
    const finiteItems = items.filter((item) => {
      const limit = numericLimit(item.limitValue)
      return Number.isFinite(limit) && limit > 0
    })

    if (finiteItems.length === 0) {
      return {
        avgPercentage: 0,
        trackedCount: items.length,
        hasFinite: false,
      }
    }

    const totalPct = finiteItems.reduce((acc, item) => {
      const limit = numericLimit(item.limitValue)
      const pct = Math.min((item.currentUsage / limit) * 100, 100)
      return acc + pct
    }, 0)

    const avg = Math.round(totalPct / finiteItems.length)
    return {
      avgPercentage: avg,
      trackedCount: items.length,
      hasFinite: true,
    }
  }, [usage])

  const chartConfig = useMemo(() => ({
    used: {
      label: 'Used Capacity',
      color: quotaStats.avgPercentage >= 85 ? 'oklch(0.6368 0.2078 25.3313)' : 'oklch(0.6231 0.1880 259.8145)',
    },
    available: {
      label: 'Available',
      color: 'oklch(0.9276 0.0058 264.5313)',
    },
  } satisfies ChartConfig), [quotaStats.avgPercentage])

  if (isLoading || !status) {
    return <StatCardsSkeleton label="Loading subscription statistics" count={4} />
  }

  const getStatusBadge = (statusValue: SubscriptionStatus) => {
    switch (statusValue) {
      case SubscriptionStatus.ACTIVE:
        return <Badge variant="success">Active</Badge>
      case SubscriptionStatus.TRIALING:
        return <Badge variant="warning">Trial</Badge>
      case SubscriptionStatus.EXPIRED:
        return <Badge variant="destructive">Expired</Badge>
      case SubscriptionStatus.CANCELLED:
        return <Badge variant="secondary">Cancelled</Badge>
      default:
        return <Badge variant="outline">{statusValue}</Badge>
    }
  }

  return (
    <section aria-label="Subscription overview" className={STAT_GRID_CLASS}>
      <StatCard>
        <StatText
          title="Current Plan"
          value={status.planName || 'None'}
          caption={
            <span className="flex items-center gap-1.5 pt-0.5">
              {status.status && getStatusBadge(status.status)}
              {status.cancelAtPeriodEnd && (
                <span className="text-[11px] font-medium text-destructive">Cancels at period end</span>
              )}
            </span>
          }
        />
      </StatCard>

      <StatCard>
        <StatText
          title="Billing Period"
          value={
            status.currentPeriodStart && status.currentPeriodEnd ? (
              <span className="block truncate text-base font-bold tabular-nums text-foreground sm:text-lg">
                {formatDate(status.currentPeriodStart)}{' '}
                <span className="font-normal text-muted-foreground/60">/</span>{' '}
                {formatDate(status.currentPeriodEnd)}
              </span>
            ) : (
              <span className="text-xl font-bold text-muted-foreground">—</span>
            )
          }
          caption={
            status.currentPeriodStart && status.currentPeriodEnd ? (
              daysRemaining !== null ? (
                <span className="font-medium text-primary">
                  {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} remaining
                </span>
              ) : (
                'Active period'
              )
            ) : (
              'No active billing period'
            )
          }
        />
      </StatCard>

      <StatCard>
        <StatText
          title="Price"
          value={status.planPrice != null ? `₹${status.planPrice}` : '—'}
          caption={
            status.planPrice != null
              ? status.planPrice > 0
                ? 'Current recurring price'
                : 'Free of charge'
              : 'No billing charge'
          }
        />
      </StatCard>

      <StatCard>
        <StatText
          title="Resource Capacity"
          value={quotaStats.hasFinite ? `${quotaStats.avgPercentage}%` : 'Unlimited'}
          caption={`${quotaStats.trackedCount} ${quotaStats.trackedCount === 1 ? 'quota' : 'quotas'} tracked`}
        />
        <DonutChart
          data={[
            {
              segment: 'used',
              value: quotaStats.avgPercentage,
              fill: quotaStats.avgPercentage >= 85 ? 'oklch(0.6368 0.2078 25.3313)' : 'oklch(0.6231 0.1880 259.8145)',
            },
            {
              segment: 'available',
              value: Math.max(0, 100 - quotaStats.avgPercentage),
              fill: 'oklch(0.9276 0.0058 264.5313)',
            },
          ]}
          config={chartConfig}
          center={quotaStats.hasFinite ? `${quotaStats.avgPercentage}%` : '100%'}
          caption="Capacity"
        />
      </StatCard>
    </section>
  )
}
